<template>
  <section class="content modal content__modal flat-scroll">
    <div
      :class="['content__header', isSonarPlaylist && 'content__header--sonar']"
    >
      <div
        class="btn btn--small btn--tertiary content__header-nav-wrapper"
        @click="$emit('close-content')"
      >
        <ArrowLeftIcon />
        <div class="ine-nav">Back to {{ backLabel }}</div>
      </div>
      <img
        v-if="isSonarPlaylist"
        class="content__header-logo"
        src="./../../assets/images/sonar-playlist.png"
        alt="Sonar Playlist Logo"
      />
    </div>
    <ActionLoader
      v-if="!isContentReady || !playlistContentAvailable"
      :size="1"
      class="content__loader content__loader--modal"
    />
    <div v-else class="content__wrapper">
      <div class="content__info-wrapper">
        <div class="content__title-wrapper">
          <div class="modal__title-wrapper content__title-container">
            <h4 class="modal__title">{{ playlist.name }}</h4>
            <div v-if="playlistDuration" class="content__title-duration">
              <div class="pill pill--duration">
                {{ playlistDuration }}
              </div>
            </div>
          </div>
          <h6 class="modal__info">{{ playlist.description }}</h6>
        </div>
        <PlaylistAssignedBy
          v-bind="{
            playlist,
            isCardLayout: false,
            isSonarPlaylist,
          }"
        />
      </div>
      <div class="content__container">
        <ActionLoader
          v-if="!isContentReady || !playlistContentAvailable"
          :size="1"
          class="content__loader"
        />
        <div
          v-for="(group, index) in playlistContent"
          v-else
          :key="index"
          :class="[
            'content__group',
            showSonarBanner(group) && 'content__group--assessment',
          ]"
        >
          <Topic
            :isUser="true"
            :topic="group"
            :topicProgress="getTopicProgress(group)"
            :progress="playlistProgress"
            :type="topicType"
            :parentId="playlistId"
          />
          <div
            v-if="showSonarBanner(group)"
            class="content__group-banner-container"
          >
            <h3 class="content__group-banner-title">
              Congratulations, this section is complete!
            </h3>
            <h4
              :class="[
                'content__group-banner-label',
                !playlistAssessment(group).assessmentId &&
                  'content__group-banner-label--cooling-off',
              ]"
            >
              {{ playlistAssessment(group).label }}
            </h4>
            <button
              v-if="playlistAssessment(group).assessmentId"
              class="btn btn--small btn--primary content__group-banner-cta"
              @click="goToAssessment(group)"
            >
              Test me
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import Topic from '@/components/content/Topic.vue'
import PlaylistAssignedBy from '@/components/playlists/PlaylistAssignedBy.vue'
import CONSTANTS from '@/constants'
import { ArrowLeftIcon } from 'vue-feather-icons'
import { isEqual, isEmpty } from 'lodash'
import { formatDistanceToNowStrict } from 'date-fns'

export default {
  name: 'PlaylistContent',
  components: {
    ActionLoader,
    ArrowLeftIcon,
    Topic,
    PlaylistAssignedBy,
  },
  props: {
    loading: {
      type: Boolean,
      required: true,
    },
    playlistId: {
      type: String,
      required: true,
    },
    isSonarPlaylist: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      topicType: CONSTANTS.PARENT_CONTENT_TYPES_KEYS.PLAYLIST,
      fetchedAssessments: [],
      isLoading: false,
    }
  },
  computed: {
    ...mapGetters('playlists', ['playlistById']),
    ...mapState({
      playlist: state => state.content.currentParent,
      currentVideos: state => state.content.currentVideos,
    }),
    playlistContent() {
      const playlist = this.playlistById(this.playlistId)
      return this.playlist ? this.playlist?.content : playlist.content
    },
    playlistProgress() {
      const userProgress = this.playlist.content.reduce(
        (quantity, group) => quantity + this.groupProgress(group),
        0
      )

      return `${userProgress}`
    },
    playlistContentAvailable() {
      return this.playlistContent?.length
    },
    playlistDuration() {
      const { duration_in_seconds: duration } = this.playlist
      return this.$duration.durationFromSecs(duration)
    },
    backLabel() {
      return this.$route.name === CONSTANTS.SONAR.ROUTES.HOME
        ? 'Skill Sonar'
        : 'all playlists'
    },
    isContentReady() {
      return !this.isLoading && !this.loading
    },
  },
  created() {
    const body = document.querySelector('body')
    body.classList.add('modal-open')
  },
  destroyed() {
    const body = document.querySelector('body')
    body.classList.remove('modal-open')
  },
  methods: {
    ...mapActions({
      getAssessmentsBySkillArea: 'sonar/getAssessmentsBySkillArea',
    }),
    isCourseFinished(course) {
      const progress = course.user_status?.progress || 0
      return progress === 1
    },
    isItemFinished(item) {
      let isFinished = false
      switch (item?.content_type) {
        case CONSTANTS.PLAYLIST_CONTENT_TYPES.COURSE:
          isFinished = this.isCourseFinished(item)
          break
        case CONSTANTS.PLAYLIST_CONTENT_TYPES.LINK:
          isFinished =
            item?.user_status === CONSTANTS.CONTENT_USER_STATUS.VISITED
          break
        case CONSTANTS.CONTENT_TYPES.VIDEO:
        case CONSTANTS.CONTENT_TYPES.LAB:
          isFinished =
            item?.user_status === CONSTANTS.CONTENT_USER_STATUS.FINISHED
          break
      }

      return isFinished
    },
    groupProgress(group) {
      return group.content.filter(item => this.isItemFinished(item)).length
    },
    getCurrentContent({ type, uuid }) {
      switch (type) {
        case 'video':
          return this.currentVideos.find(video => video.id === uuid)
        case 'learning-path':
        case 'lab':
        case 'course':
        case 'link':
          return this.playlist?.content
            ?.flatMap(g => g.content)
            .find(item => item.uuid === uuid && item.content_type === type)
        default:
          return { user_status: CONSTANTS.CONTENT_USER_STATUS.UNSTARTED }
      }
    },

    getTopicProgress(group) {
      const progress = { total: group.content.length, completed: 0 }

      group.content.map(level => {
        const { content_type: type, uuid } = level
        const current = this.getCurrentContent({ type, uuid })
        if (current) {
          const { user_status } = current
          if (
            user_status &&
            (user_status === CONSTANTS.CONTENT_USER_STATUS.FINISHED ||
              user_status === CONSTANTS.CONTENT_USER_STATUS.VISITED)
          )
            progress.completed += 1
          if (
            [
              CONSTANTS.PLAYLIST_CONTENT_TYPES.LEARNING_PATH,
              CONSTANTS.PLAYLIST_CONTENT_TYPES.COURSE,
            ].includes(type) &&
            user_status &&
            user_status.progress === 1
          )
            progress.completed += 1
        }
      })
      return progress
    },
    isGroupProgressCompleted(group) {
      const progress = this.getTopicProgress(group)
      return progress.total > 0 && progress.total === progress.completed
    },
    showSonarBanner(group) {
      return (
        this.isSonarPlaylist &&
        this.isGroupProgressCompleted(group) &&
        !isEmpty(this.findAssessment(group))
      )
    },
    playlistAssessment(group) {
      const assessment = this.findAssessment(group)
      const assessmentId = assessment?.user_status?.can_retake
        ? assessment.id
        : null
      let label =
        'Ready to measure your progress? Assess your skills to see if your training has paid off.'
      if (!assessmentId) {
        const retakeDate = assessment?.user_status?.datetime_to_retake
        const remaining = retakeDate
          ? `in ${formatDistanceToNowStrict(new Date(retakeDate))}`
          : ''
        label = `Reassess ${remaining} to see if your training has paid off.`
      }
      return {
        assessmentId,
        label,
      }
    },
    findAssessment(group) {
      return this.fetchedAssessments?.find(
        a => a.skill_areas[0]?.id === group.uuid
      )
    },
    goToAssessment(group) {
      const assessment_id = this.findAssessment(group)?.id
      this.$emit('go-to-assessment', assessment_id)
    },
    async getAssessments() {
      this.isLoading = true
      const skill_area_ids = this.playlistContent
        ?.map(c => c.uuid)
        .filter(c => !isEmpty(c))
      if (skill_area_ids?.length)
        this.fetchedAssessments = await this.getAssessmentsBySkillArea(
          skill_area_ids
        )
      this.isLoading = false
    },
  },
  watch: {
    playlistContent: {
      immediate: true,
      handler(newValue, oldValue) {
        if (!this.isSonarPlaylist || !newValue) return
        const shouldFetch = newValue.length && !isEqual(newValue, oldValue)
        if (shouldFetch) this.getAssessments()
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.content {
  &__header {
    position: relative;
    height: 86px;

    background: linear-gradient(
      180deg,
      rgba(89, 54, 228, 1) 0%,
      rgba(0, 0, 0, 0) 100%
    );

    display: flex;
    justify-content: center;
    align-items: center;
    img {
      width: 175px;
      height: 64px;
    }
    &-nav-wrapper {
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 10px;
      padding: 8px 16px;
      color: var(--white);
      left: 20px;
      top: 20px;
      @include mobile {
        left: 20px;
        top: 20px;
      }
      svg {
        width: 16px;
        height: 16px;
        fill: none !important;
      }
    }
    &--sonar {
      height: 158px;
      background: linear-gradient(0deg, #000 0%, rgba(17, 17, 17, 0) 100%),
        var(--business-purple-glass);
    }
  }
  &__wrapper {
    background: var(--black);
    padding: 0 20px 20px;
    @include mobile {
      padding: 0 50px 50px;
    }
    @include tablet {
      padding: 0 100px 100px;
    }
  }
  &__title-wrapper {
    flex: 1 1;
  }
  &__title-container {
    align-items: flex-start;
  }
  &__title-duration {
    min-width: fit-content;
    padding-top: 4px;
  }
  &__info-wrapper {
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    margin: 20px 0;
    gap: 20px;
    @include mobile {
      flex-direction: row;
      gap: 30px;
    }
  }
  &__container {
    display: flex;
    flex-direction: column;
    gap: 20px;
    border-radius: 0px 0px 10px 10px;
  }
  &__modal {
    background: var(--black);
  }
  &__loader {
    display: flex;
    justify-content: center;
    &--modal {
      margin-top: 25vh;
    }
  }
  &__check-icon {
    color: var(--perfect-gray);
    margin-right: 10px;

    @include tablet {
      margin-right: 20px;
    }

    &--checked {
      color: var(--positive-light);
    }
  }
  &__section-title {
    font-family: var(--font-medium);
    font-size: rem(18px);
    line-height: 24px;
    color: var(--white);
  }
  &__course-list {
    display: flex;
    flex-direction: column;
    margin-top: 10px;
    gap: 10px 0px;
  }
  &__course-item {
    display: flex;
    align-items: center;
  }
  &__group {
    &-banner {
      &-container {
        display: flex;
        flex-direction: column;
        padding: 20px 30px;
        gap: 5px;
        background: linear-gradient(
          90deg,
          #2f0559 4.2%,
          #863ad2 52.87%,
          #b736e4 97.03%
        );
        border-radius: 0 0 10px 10px;
        @include tablet {
          gap: 20px;
          flex-direction: row;
          align-items: center;
          height: 76px;
        }
      }
      &-title {
        color: var(--white);
        font-size: rem(18);
        line-height: rem(24);
        font-weight: 500;
        margin: 0;
      }
      &-label {
        color: var(--white);
        font-size: rem(14);
        line-height: rem(20);
        margin: 0;
        &--cooling-off {
          @include tablet {
            margin-left: auto;
          }
        }
      }
      &-cta {
        width: 100%;
        margin-top: 10px;
        @include tablet {
          margin: 0 0 0 auto;
          width: fit-content;
        }
      }
    }
    &--assessment {
      ::v-deep .group__topic--modal {
        border-radius: 5px 5px 0 0;
      }
    }
  }
}
</style>
